import { type IDiscountTargetCriteria } from '@mahawi/eshop-common/src/types';
import { Button, InputNumber, Select, Space, Spin } from 'antd';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import {
  customerSearch,
  discountSegmentPreview,
  type ICustomerSearchResult,
} from '../../reducers/crm-customers';
import { SPACING } from '../../theme/tokens';

let debounceTimeout: ReturnType<typeof setTimeout> | undefined;

function CustomerPicker({
  value,
  onChange,
  disabled,
}: {
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const countries = useSelector((s: RootState) => s.Countries.countries);
  const searchResults = useSelector(
    (s: RootState) => s.CrmCustomers.searchResults,
  );
  const previewResults = useSelector(
    (s: RootState) => s.CrmCustomers.segmentPreviewResults,
  );
  const inProcess = useSelector((s: RootState) => s.CrmCustomers.inProcess);
  const [searching, setSearching] = useState(false);

  const [filterCountry, setFilterCountry] = useState<string | undefined>();
  const [filterMinOrderCount, setFilterMinOrderCount] = useState<
    number | undefined
  >();
  const [filterMinOrderValue, setFilterMinOrderValue] = useState<
    number | undefined
  >();
  const [filterWindowDays, setFilterWindowDays] = useState<
    number | undefined
  >();

  const combinedResults = useMemo(() => {
    const byUuid = new Map<string, ICustomerSearchResult>();
    for (const customer of [
      ...(searchResults ?? []),
      ...(previewResults ?? []),
    ]) {
      byUuid.set(customer.uuid, customer);
    }
    return [...byUuid.values()];
  }, [searchResults, previewResults]);

  const options = useMemo(
    () =>
      combinedResults.map((customer) => ({
        value: customer.uuid,
        label: [customer.firstName, customer.lastName, customer.email]
          .filter(Boolean)
          .join(' — '),
      })),
    [combinedResults],
  );

  const handleSearch = (query: string): void => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    if (query.trim().length === 0) {
      return;
    }

    setSearching(true);
    debounceTimeout = setTimeout(() => {
      dispatch(customerSearch(query));
      setSearching(false);
    }, 300);
  };

  const hasFilter: boolean =
    filterCountry !== undefined ||
    filterMinOrderCount !== undefined ||
    filterMinOrderValue !== undefined;

  const handleFindCustomers = (): void => {
    const criteria: IDiscountTargetCriteria = {
      countryTypeIsoCode: filterCountry,
      minOrderCount: filterMinOrderCount,
      minOrderValue: filterMinOrderValue,
      orderCountWindowDays:
        filterMinOrderCount !== undefined || filterMinOrderValue !== undefined
          ? filterWindowDays
          : undefined,
    };

    dispatch(discountSegmentPreview(criteria));
  };

  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      <Space wrap size={SPACING.sm}>
        <Select
          placeholder="Country"
          allowClear
          style={{ width: 160 }}
          value={filterCountry}
          onChange={(next: string | undefined): void => setFilterCountry(next)}
          disabled={disabled}
          showSearch={{ optionFilterProp: 'label' }}
          options={countries?.map((country) => ({
            value: country.isoCode,
            label: country.name,
          }))}
        />
        <InputNumber
          placeholder="Min. orders"
          min={1}
          style={{ width: 110 }}
          value={filterMinOrderCount}
          onChange={(next: number | null): void =>
            setFilterMinOrderCount(next ?? undefined)
          }
          disabled={disabled}
        />
        <InputNumber
          placeholder="Min. order value €"
          min={0}
          style={{ width: 150 }}
          value={filterMinOrderValue}
          onChange={(next: number | null): void =>
            setFilterMinOrderValue(next ?? undefined)
          }
          disabled={disabled}
        />
        <InputNumber
          placeholder="Within last N days"
          min={1}
          style={{ width: 140 }}
          value={filterWindowDays}
          onChange={(next: number | null): void =>
            setFilterWindowDays(next ?? undefined)
          }
          disabled={
            disabled ||
            (filterMinOrderCount === undefined &&
              filterMinOrderValue === undefined)
          }
        />
        <Button
          onClick={handleFindCustomers}
          disabled={disabled || !hasFilter}
          loading={inProcess}
        >
          Find customers
        </Button>
      </Space>

      <Select
        mode="multiple"
        showSearch={{ onSearch: handleSearch, filterOption: false }}
        value={value}
        onChange={onChange}
        disabled={disabled}
        loading={searching || inProcess}
        notFoundContent={searching || inProcess ? <Spin size="small" /> : null}
        placeholder="Search by customer email, or use the filters above"
        options={options}
      />
    </Space>
  );
}

export default CustomerPicker;
